/* eslint-disable @typescript-eslint/no-explicit-any */
import type { AxiosRequestConfig } from "axios";

import request from "./request";

import DatasetEnum from "@/api/dataset/enums";

import AlgorithmEnum from "@/api/algorithm/enums";

export type PublishDemandParams = {
  description: string;
  id?: number;
};

// 上传方式类型
export type UploadMethod = "tus" | "cos"; // "tus": TUS, "cos": COS

// 上传方式查询响应
export interface UploadMethodResponse {
  uploadType: UploadMethod;
  uploadUrl: string; // 上传地址，根据uploadType决定含义
}

// downloadList 的新格式：{ "0": [{ url: string }], "1": [{ url: string }], ... }
export interface DownloadList {
  [key: string]: Array<{ url: string }>;
}

export interface OrderDetail {
  id: number;
  datasetId: number;
  datasetName: string;
  price: number;
  taskCode: string;
  createTime: number;
  createTimeSql: string;
  fileUrl: string | null; // 保留字段，但不再使用
  isAssessmentReport: number;
  status: number;
  payStatus: number;
  taskStatus: number;
  showRefundFlag: boolean;
  privateKey: string | null;
  pattern: string;
  taskReady: 0 | 1;
  publishStatus: 0 | 1 | 2 | 3;
  clientDownloadType: string; // 保留字段，但不再使用
  clientDownloadUrl: string | null; // 保留字段，但不再使用
  downloadList?: DownloadList; // 新增字段，格式：{ "0": [{ url: string }], ... }
  checksumUrl?: string; // 新增字段，验证文件URL
}

export interface CosTmpCredentialResponse {
  tmpSecretId: string;
  tmpSecretKey: string;
  sessionToken: string;
  bucketName: string;
  region: string;
  fileKey: string;
}

const isDev =
  process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test";
const url = !isDev ? "" : "/api";

/**
 * 查询用户已发布的数据
 */
export function getMyData(options: { datasetId: number }, token: string) {
  const config: AxiosRequestConfig = {
    url: `${url}${DatasetEnum.GET_DATA_CHAIN_STATUS}`,
    method: "post",
    headers: {
      Referer: "app://.",
      token: token,
    },
    data: options,
  };
  return request<0 | 1>(config);
}

/**
 * 发布数据｜修改数据
 */
export function putData(
  options: {
    datasetName: string;
    price: number;
    pattern: string;
    datasetSize: number; // 压缩后的大小
    datasetActualSize?: number; // 实际大小（解压后）
    metaJson: string;
    auditParam: string;
    id?: number;
    tag?: string;
    description?: string;
    // descFormat：描述内容格式（html | markdown）
    descFormat?: "html" | "markdown";
    descriptionTxt?: string;
    imageList: string;
    estimateAlgorithmList?: {
      algorithmId: number;
      auditParam: string;
      forwardSig: string;
    }[];
    auditType: 0 | 1;
    serverType: 0 | 1;
    serverUrl?: string;
    serverPublicKey?: string;
    rcrFromSeller?: number;
  },
  token: string
) {
  const config: AxiosRequestConfig = {
    url: `${url}${DatasetEnum.PUT_DATA}`,
    method: "post",
    headers: {
      Referer: "app://.",
      token: token,
    },
    data: options,
  };
  console.log("putData options", options);
  return request<{
    datasetId: number;
    datasetCode: string;
  }>(config);
}

// 获取托管算法（购买数据时用的算法）列表
export function getAlgorithmList() {
  const config: AxiosRequestConfig = {
    url: `${url}${DatasetEnum.GET_ALGORITHM_LIST}`,
    method: "post",
  };
  return request<
    {
      id: number;
      publicKey: string;
      uploadUrl: string;
    }[]
  >(config);
}

/**
 * 可以评估的算法查询接口
 */
export function getAssessmentAlgorithm(pattern: string) {
  const config: AxiosRequestConfig = {
    url: `${url}${AlgorithmEnum.ALGORITHM_LIST}`,
    method: "post",
    data: {
      pattern,
    },
  };
  return request<
    {
      id: number;
      algorithmName: string;
      sample: string;
      codeUrl: string;
      templateHtml: string;
      hash: string;
    }[]
  >(config);
}

export const getDict = (options: { dataType: string; pid: number }) => {
  return request<
    {
      dataExtra: string;
      dataType: string;
      dataValue: string;
    }[]
  >({
    url: `${url}/common/dict`,
    method: "post",
    data: options,
  });
};
export const getDownloadUrl = () => {
  return request<{
    clientDownloadType: "0" | "1";
    clientDownloadUrl: string | null;
  }>({
    url: `${url}/common/clientDownloadUrl`,
    method: "post",
  });
};

export const getMyOrderList = (taskIdList: number[], token: string) => {
  return request<{
    data: OrderDetail[];
  }>({
    url: `${url}/system/task/taskList`,
    method: "post",
    data: {
      pageNo: 1,
      pageSize: taskIdList.length,
      taskIdList: taskIdList,
    },
    headers: {
      Referer: "app://.",
      token: token,
    },
  });
};

export function getCosTmpCredential(
  cosStorageServiceUrl: string,
  uploadFileName: string,
  token: string
) {
  const urlObj = new URL("cos/tmp/credential", cosStorageServiceUrl);
  const config: AxiosRequestConfig = {
    url: urlObj.toString(),
    method: "post",
    data: {
      uploadFileName,
    },
    headers: {
      Referer: "app://.",
      token: token,
    },
    timeout: 10000,
  };

  return request<CosTmpCredentialResponse>(config);
}
export const checkTask = (orderCode: string, token: string) => {
  return request<{ orderCode: string; publishStatus: 0 | 1 | 2 | 3 }>({
    url: `${url}/task/check/task`,
    method: "post",
    data: {
      orderCode,
    },
    headers: {
      Referer: "app://.",
      token: token,
    },
  });
};

/**
 * 发布需求
 */
export function publishDemand(params: PublishDemandParams) {
  const config: AxiosRequestConfig = {
    url: `${url}${DatasetEnum.PUBLISH_DEMAND}`,
    method: "post",
    data: params,
  };
  return request(config);
}
// 1. 查询上链交易参数接口
export type ChainTransactionInitResponse = {
  fromAddress: string;
  nonce: number;
  gasPrice: number;
  gasLimit: number;
  contractAddress: string;
  chainId: number;
  functionName: string;
  dataOnChainHash?: string;
  requestOnChainHash?: string;
  resultOnChainHash?: string;
};

export function initDataTransaction(
  token: string,
  functionName?: string,
  orderCode?: string
) {
  const config: AxiosRequestConfig = {
    url: `${url}/chain/transaction/initDataTransaction`,
    method: "post",
    headers: {
      Referer: "app://.",
      token: token,
    },
    data: {
      functionName: functionName ? functionName : "createStaticData",
      orderCode,
    },
  };
  return request<ChainTransactionInitResponse>(config);
}
// 4. 数据集发布时发起交易上链dryRun接口
export type DryRunDataTransactionParams = {
  functionName: string;
  contractAddress: string;
  nonce: string;
  gasPrice: string;
  gasLimit: string;
  functionEncoder: string;
};

export function dryRunDataTransaction(
  params: DryRunDataTransactionParams,
  token: string
) {
  const config: AxiosRequestConfig = {
    url: `${url}/chain/transaction/dryRunDataTransaction`,
    method: "post",
    data: params,
    headers: {
      Referer: "app://.",
      token: token,
    },
  };
  return request<string>(config);
}

// 5. 数据集发布时发起交易上链接口
export type SendDataTransactionParams = {
  UUID: string;
  signedTransactionTx: string;
};

export type SendDataTransactionResponse = {
  fromAddress: string;
  transactionHash: string;
  UUID: string;
};

export function sendDataTransaction(
  params: SendDataTransactionParams,
  token: string,
  functionName?: string
) {
  const config: AxiosRequestConfig = {
    url: `${url}/chain/transaction/sendDataTransaction`,
    method: "post",
    data: {
      ...params,
      functionName: functionName ? functionName : "createStaticData",
    },
    headers: {
      Referer: "app://.",
      token: token,
    },
  };
  return request<SendDataTransactionResponse>(config);
}

// 6. 检查数据集状态接口
export type CheckDatasetStatusParams = {
  datasetCode: string;
};

export type CheckDatasetStatusResponse = {
  datasetCode: string;
  saveStatus: 0 | 1;
  publishStatus: 0 | 1 | 2 | 3;
  //0 未发布交易上链 1 发布中(已调用交易上链) 2 发布成功(最终异步上链成功) 3 发布失败(最终异步上链失败)
};

export function checkDatasetStatus(
  params: CheckDatasetStatusParams,
  token: string
) {
  const config: AxiosRequestConfig = {
    url: `${url}/dataset/check/dataset`,
    method: "post",
    data: params,
    headers: {
      Referer: "app://.",
      token: token,
    },
  };
  return request<CheckDatasetStatusResponse>(config);
}

// 7. 查询上传方式接口
export function getUploadMethod(token: string) {
  const config: AxiosRequestConfig = {
    url: `${url}/dataset/upload/method`,
    method: "post",
    headers: {
      Referer: "app://.",
      token: token,
    },
  };
  return request<UploadMethodResponse>(config);
}
